import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import Fuse from "fuse.js";
import {
  MagnifyingGlassIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  ArrowLeftOnRectangleIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  ShoppingBagIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import AccentButton from "../ui/AccentButton";
import LanguageSelector from "../ui/LanguageSelector";
import { useAuth } from "../../contexts/AuthContext";
import { useCart } from "../../contexts/CartContext";
import { useTranslation } from "react-i18next";
import logo from "../../assets/images/icons/Logo_orange.png";

const UniversalHeader = () => {
  const { t } = useTranslation();
  const { user, logout, apiCall } = useAuth();
  const { getCartItemsCount, getTotalItems } = useCart();

  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);

  const [showUserMenu, setShowUserMenu] = useState(false);

  const [realItems, setRealItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  const searchRef = useRef(null);
  const userMenuRef = useRef(null);
  const navigate = useNavigate();

  const unreadBadgeText =
    unreadMessagesCount > 99 ? "99+" : String(unreadMessagesCount);

  const cartItemsCount =
    typeof getTotalItems === "function"
      ? getTotalItems()
      : typeof getCartItemsCount === "function"
        ? getCartItemsCount()
        : 0;

  const cartBadgeText = cartItemsCount > 99 ? "99+" : String(cartItemsCount);

  const getCompanyId = () => {
    return (
      user?.company_id ||
      user?.companyId ||
      user?.company?.id ||
      user?.company?.company_id ||
      ""
    );
  };

  const isCompanyUser = () => {
    const role = String(user?.role || "").toLowerCase();

    return (
      role === "company_owner" ||
      role === "company" ||
      role === "employee"
    );
  };

  const formatSubscriptionDate = (value) => {
    if (!value) {
      return "—";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const getSubscriptionLabel = () => {
    if (subscriptionLoading) {
      return "Checking subscription...";
    }

    if (!subscriptionInfo) {
      return "Subscription status unavailable";
    }

    if (subscriptionInfo.is_active) {
      return "Active subscription";
    }

    return "Subscription inactive";
  };
  const getSubscriptionPlanName = () => {
    const planKey = String(
      subscriptionInfo?.plan_key ||
        subscriptionInfo?.plan ||
        subscriptionInfo?.planKey ||
        "",
    ).toLowerCase();
  
    switch (planKey) {
      case "basic":
        return "Starter Plan";
      case "pro":
        return "Pro Plan";
      case "business":
        return "Business Plan";
      case "elite":
        return "Elite Plan";
      default:
        return subscriptionInfo?.is_active ? "Subscription Plan" : "";
    }
  };

  const getSubscriptionExpiresLabel = () => {
    const expiresAt =
      subscriptionInfo?.expires_at ||
      subscriptionInfo?.subscription_expires_at ||
      subscriptionInfo?.current_period_end ||
      "";

    if (!expiresAt) {
      return "No expiration date";
    }

    return `Expires: ${formatSubscriptionDate(expiresAt)}`;
  };

  useEffect(() => {
    const fetchItems = async () => {
      try {
        setLoading(true);

        const response = await fetch("/api/v1/public/marketplace");
        const data = await response.json();

        if (data.success) {
          setRealItems(data.listings || []);
        }
      } catch (error) {
        console.error("Ошибка загрузки данных для поиска:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, []);

  useEffect(() => {
    if (!user || !apiCall) {
      setUnreadMessagesCount(0);
      return undefined;
    }

    let isMounted = true;

    const loadUnreadMessagesCount = async () => {
      try {
        const response = await apiCall("/chats/unread-count");

        const count = Number(
          response?.data?.unread_count ??
            response?.data?.count ??
            response?.unread_count ??
            response?.count ??
            0,
        );

        if (isMounted) {
          setUnreadMessagesCount(Number.isFinite(count) ? count : 0);
        }
      } catch (error) {
        if (isMounted) {
          setUnreadMessagesCount(0);
        }
      }
    };

    loadUnreadMessagesCount();

    const intervalId = window.setInterval(loadUnreadMessagesCount, 15000);

    const handleRefresh = () => {
      loadUnreadMessagesCount();
    };

    window.addEventListener("zootel-chat-unread-refresh", handleRefresh);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
      window.removeEventListener("zootel-chat-unread-refresh", handleRefresh);
    };
  }, [apiCall, user]);

  useEffect(() => {
    if (!user || !apiCall || !isCompanyUser()) {
      setSubscriptionInfo(null);
      return undefined;
    }

    const companyId = getCompanyId();

    if (!companyId) {
      setSubscriptionInfo(null);
      return undefined;
    }

    let isMounted = true;

    const loadSubscriptionStatus = async () => {
      try {
        setSubscriptionLoading(true);

        const response = await apiCall(
          `/billing/subscription-status?company_id=${encodeURIComponent(
            companyId,
          )}`,
        );

        if (isMounted) {
          setSubscriptionInfo({
            status:
              response?.status ||
              response?.data?.status ||
              response?.subscription_status ||
              "inactive",
            is_active: Boolean(
              response?.is_active ??
                response?.data?.is_active ??
                response?.active ??
                false,
            ),
            plan_key:
              response?.plan_key ||
              response?.data?.plan_key ||
              response?.plan ||
              response?.data?.plan ||
              response?.planKey ||
              response?.data?.planKey ||
              "",
            expires_at:
              response?.expires_at ||
              response?.data?.expires_at ||
              response?.subscription_expires_at ||
              response?.data?.subscription_expires_at ||
              response?.current_period_end ||
              response?.data?.current_period_end ||
              "",
          });
        }
      } catch (error) {
        console.error("Load subscription status error:", error);

        if (isMounted) {
          setSubscriptionInfo(null);
        }
      } finally {
        if (isMounted) {
          setSubscriptionLoading(false);
        }
      }
    };

    loadSubscriptionStatus();

    const intervalId = window.setInterval(loadSubscriptionStatus, 60000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [apiCall, user]);

  const fuse = useMemo(() => {
    return new Fuse(realItems, {
      keys: ["name", "description", "company"],
      threshold: 0.3,
      distance: 100,
    });
  }, [realItems]);

  const handleSearchInputChange = (value) => {
    setSearchQuery(value);

    if (value.length < 2) {
      setSearchSuggestions([]);
      return;
    }

    const matches = fuse.search(value).map((res) => ({
      type: res.item.type === "service" ? "service" : "product",
      name: res.item.name,
      icon: res.item.type === "service" ? "🛍️" : "📦",
      id: res.item.id,
    }));

    const cityMatches = ["London", "Berlin", "Paris"]
      .filter((city) => city.toLowerCase().includes(value.toLowerCase()))
      .map((city) => ({ type: "city", name: city, icon: "📍" }));

    setSearchSuggestions([...matches, ...cityMatches]);
    setShowSearchSuggestions(true);
  };

  const openChatPopup = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (user?.role === "company_owner" || user?.role === "employee") {
      navigate("/company/chat");
      return;
    }

    window.dispatchEvent(new Event("open-zootel-chat"));
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchSuggestions(false);
      }

      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClick);

    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-50 h-16 flex items-center">
      <div className="max-w-7xl mx-auto px-6 w-full flex items-center justify-between">
        <div className="flex items-center flex-shrink-0 mr-10">
          <Link to="/" className="group">
            <img
              src={logo}
              alt="Zootel"
              className="h-8 w-auto transition-transform duration-300 group-hover:scale-105"
              style={{ minWidth: "32px" }}
            />
          </Link>
        </div>

        <nav className="hidden lg:flex items-center gap-6 mr-auto">
          <Link
            to="/services"
            className="nav-link-premium text-slate-400 hover:text-[#0A1128] transition-colors"
          >
            {t("nav.services")}
          </Link>

          <Link
            to="/pricing"
            className="nav-link-premium text-slate-400 hover:text-[#0A1128] transition-colors"
          >
            {t("nav.pricing")}
          </Link>

          <Link
            to="/about"
            className="nav-link-premium text-slate-400 hover:text-[#0A1128] transition-colors"
          >
            {t("nav.about")}
          </Link>
        </nav>

        <div
          className="hidden lg:block flex-1 max-w-sm mx-8 relative"
          ref={searchRef}
        >
          <div className="relative group">
            <MagnifyingGlassIcon
              className={`absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors ${
                loading
                  ? "text-orange-500 animate-pulse"
                  : "text-gray-400 group-focus-within:text-[#F65814]"
              }`}
            />

            <input
              type="text"
              placeholder={
                loading ? t("nav.loading") : t("nav.search_placeholder")
              }
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border-none rounded-xl focus:ring-1 focus:ring-[#F65814]/20 focus:bg-white transition-all font-bold text-[12px] placeholder:text-slate-300"
              value={searchQuery}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              onFocus={() => setShowSearchSuggestions(true)}
            />
          </div>

          {showSearchSuggestions && searchSuggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-50 overflow-hidden py-1 z-[60]">
              {searchSuggestions.map((suggestion, index) => (
                <button
                  key={`${suggestion.type}-${suggestion.id || suggestion.name}-${index}`}
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setShowSearchSuggestions(false);
                    navigate(
                      `/marketplace?search=${encodeURIComponent(
                        suggestion.name,
                      )}`,
                    );
                  }}
                  className="w-full flex items-center px-4 py-2.5 hover:bg-orange-50 transition-colors text-left group"
                >
                  <span className="mr-3 text-base">{suggestion.icon}</span>

                  <span className="flex-1 font-bold text-gray-700 text-xs">
                    {suggestion.name}
                  </span>

                  <span className="text-[8px] font-black uppercase text-gray-300">
                    {suggestion.type}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          {!user && (
            <div className="hidden md:block">
              <LanguageSelector />
            </div>
          )}

          {user ? (
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={openChatPopup}
                className="p-1.5 text-slate-400 hover:text-[#F65814] transition-all relative"
                aria-label={
                  unreadMessagesCount > 0
                    ? `Чат, новых сообщений: ${unreadMessagesCount}`
                    : "Чат"
                }
                title={
                  unreadMessagesCount > 0
                    ? `Новых сообщений: ${unreadMessagesCount}`
                    : "Чат"
                }
              >
                <ChatBubbleLeftRightIcon className="h-5 w-5 stroke-[2px]" />

                {unreadMessagesCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#F65814] border-2 border-white text-white text-[9px] font-black leading-[14px] flex items-center justify-center shadow-sm">
                    {unreadBadgeText}
                  </span>
                )}
              </button>

              <Link
                to="/cart"
                className="p-1.5 text-slate-400 hover:text-[#F65814] transition-all relative"
                aria-label={
                  cartItemsCount > 0
                    ? `Корзина, товаров: ${cartItemsCount}`
                    : "Корзина"
                }
                title={
                  cartItemsCount > 0
                    ? `Товаров в корзине: ${cartItemsCount}`
                    : "Корзина"
                }
              >
                <ShoppingBagIcon className="h-5 w-5 stroke-[2px]" />

                {cartItemsCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#F65814] border-2 border-white text-white text-[9px] font-black leading-[14px] flex items-center justify-center shadow-sm">
                    {cartBadgeText}
                  </span>
                )}
              </Link>

              <div className="h-6 w-[1px] bg-slate-100" />

              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2.5 p-0.5 rounded-xl hover:bg-slate-50 transition-all"
                >
                  <div className="flex flex-col items-end hidden sm:flex">
                    <span className="text-[9px] font-black uppercase tracking-tight text-[#0A1128]">
                      {user.name || user.email?.split("@")[0]}
                    </span>

                    <span className="text-[8px] font-bold text-[#F65814] uppercase">
                      {user.role?.replace("_", " ") || "Client"}
                    </span>
                  </div>

                  <div
                    className={`h-9 w-9 rounded-xl flex items-center justify-center transition-all ${
                      showUserMenu
                        ? "bg-[#F65814] text-white shadow-md"
                        : "bg-slate-50 border border-slate-100 text-slate-400"
                    }`}
                  >
                    <UserIcon className="h-4 w-4" />
                  </div>
                </button>

                {showUserMenu && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-50 overflow-hidden z-[70] animate-in fade-in zoom-in-95 duration-150">
                    <div className="py-1.5">
                      <div className="px-4 py-2 border-b border-gray-50 mb-1">
                        <p className="text-[8px] font-black uppercase tracking-widest text-gray-400">
                          {t("nav.account")}
                        </p>

                        <p className="text-[10px] font-bold text-[#0A1128] truncate">
                          {user.email}
                        </p>

                        {isCompanyUser() && (
                          <div className="mt-2 rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
                            <div className="flex items-center gap-2">
                              {subscriptionInfo?.is_active ? (
                                <CheckCircleIcon className="h-4 w-4 text-emerald-500 shrink-0" />
                              ) : (
                                <XCircleIcon className="h-4 w-4 text-red-400 shrink-0" />
                              )}

                              <p
                                className={`text-[9px] font-black uppercase tracking-wider ${
                                  subscriptionInfo?.is_active
                                    ? "text-emerald-600"
                                    : "text-red-400"
                                }`}
                              >
                                {getSubscriptionLabel()}
                              </p>
                              {subscriptionInfo?.is_active && getSubscriptionPlanName() && (
                                <p className="mt-1 text-[10px] font-black uppercase tracking-wider text-[#0A1128]">
                                  {getSubscriptionPlanName()}
                                </p>
                              )}
                            </div>

                            <p className="mt-1 text-[9px] font-bold uppercase tracking-wide text-slate-400">
                              {getSubscriptionExpiresLabel()}
                            </p>
                          </div>
                        )}
                      </div>

                      {user.role === "super_admin" && (
                        <Link
                          to="/admin"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-3 px-4 py-2 hover:bg-orange-50 text-[#F65814] transition-colors"
                        >
                          <ShieldCheckIcon className="h-4 w-4" />

                          <span className="text-[10px] font-black uppercase tracking-wider">
                            Admin Panel
                          </span>
                        </Link>
                      )}

                      {user.role === "company_owner" && (
                        <Link
                          to="/company"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-3 px-4 py-2 hover:bg-orange-50 text-[#F65814] transition-colors"
                        >
                          <BuildingOfficeIcon className="h-4 w-4" />

                          <span className="text-[10px] font-black uppercase tracking-wider">
                            {t("nav.buisness_panel")}
                          </span>
                        </Link>
                      )}

                      <Link
                        to={
                          user.role === "company"
                            ? "/company/dashboard"
                            : "/profile"
                        }
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 text-slate-600 hover:text-[#0A1128] transition-colors"
                      >
                        <UserCircleIcon className="h-4 w-4" />

                        <span className="text-[10px] font-bold uppercase tracking-wider">
                          {t("nav.profile")}
                        </span>
                      </Link>

                      <div className="h-[1px] bg-gray-50 my-1" />

                      <LanguageSelector variant="menu-item" />

                      <div className="h-[1px] bg-gray-50 my-1" />

                      <button
                        type="button"
                        onClick={() => {
                          setShowUserMenu(false);
                          logout();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-red-50 text-red-500 transition-colors group"
                      >
                        <ArrowLeftOnRectangleIcon className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />

                        <span className="text-[10px] font-bold uppercase tracking-wider">
                          {t("nav.sing_out")}
                        </span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <Link
                to="/login"
                className="text-[10px] font-black uppercase tracking-widest text-[#0A1128] hover:text-[#F65814] transition-colors"
              >
                {t("nav.sign_in")}
              </Link>

              <AccentButton
                to="/register"
                className="text-[10px] px-4 py-2 h-9"
              >
                {t("nav.join")}
              </AccentButton>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .nav-link-premium {
          font-size: 10px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
      `}</style>
    </header>
  );
};

export default UniversalHeader;